import { Button, Alert, Checkbox, SuccessNotice, FormField, NumberInput, ConfirmDialog } from "@a2z/ui";
import { useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useUpdateStoreSettingsMutation } from "@/modules/settings/services/useUpdateStoreSettingsMutation";

type DepositMode = "none" | "shipping" | "flat" | "percent";

type Props = {
  storeSlug: string;
  depositSettings: { type: "percent" | "flat"; amount: number } | null;
  includeShippingInOrderDeposit: boolean;
};

function resolveInitialMode(
  depositSettings: Props["depositSettings"],
  includeShippingInOrderDeposit: boolean,
): DepositMode {
  if (depositSettings === null) {
    return includeShippingInOrderDeposit ? "shipping" : "none";
  }
  return depositSettings.type === "flat" ? "flat" : "percent";
}

function getPercentCallout(
  t: ReturnType<typeof useTranslations>,
  amountInput: string,
): string {
  const parsed = Number(amountInput);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) {
    return t(keys.seller.depositPercentInvalidHelper);
  }
  if (parsed === 0) {
    return t(keys.seller.depositPercentZeroHelper);
  }
  if (parsed === 100) {
    return t(keys.seller.depositPercentFullHelper);
  }
  const egpExample = Math.round(500 * parsed) / 100;
  return t(keys.seller.depositPercentPartialHelper, {
    percent: String(parsed),
    amount: String(egpExample),
  });
}

export function DepositSettingsForm({
  storeSlug,
  depositSettings,
  includeShippingInOrderDeposit,
}: Props) {
  const t = useTranslations();
  const [mode, setMode] = useState<DepositMode>(
    resolveInitialMode(depositSettings, includeShippingInOrderDeposit),
  );
  const [amountInput, setAmountInput] = useState(
    depositSettings !== null ? String(depositSettings.amount) : "",
  );
  const [includeShippingOnTop, setIncludeShippingOnTop] = useState(
    depositSettings !== null ? includeShippingInOrderDeposit : false,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const { mutateAsync: updateStoreSettings } = useUpdateStoreSettingsMutation();

  const parsedAmount = Number(amountInput);
  const resolvedAmount =
    Number.isFinite(parsedAmount) && parsedAmount >= 0 ? parsedAmount : null;

  // Amount validation error (only for flat / percent)
  const amountError: string | null =
    mode !== "flat" && mode !== "percent"
      ? null
      : resolvedAmount === null
        ? t(keys.seller.depositPercentRequired)
        : mode === "percent" && parsedAmount > 100
          ? t(keys.seller.depositPercentRequired)
          : null;

  const handleModeChange = (next: DepositMode) => {
    setMode(next);
    setSuccess(false);
  };

  const handleSubmit = () => {
    setError(null);
    setSuccess(false);
    if ((mode === "flat" || mode === "percent") && amountError) {
      setError(amountError);
      return;
    }
    if ((mode === "flat" || mode === "percent") && resolvedAmount === null) {
      setError(t(keys.seller.depositPercentRequired));
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirmedSubmit = async () => {
    setShowConfirm(false);
    setIsSubmitting(true);
    try {
      if (mode === "none") {
        await updateStoreSettings({
          storeSlug,
          clearDepositSettings: true,
          includeShippingInOrderDeposit: false,
        });
      } else if (mode === "shipping") {
        await updateStoreSettings({
          storeSlug,
          clearDepositSettings: true,
          includeShippingInOrderDeposit: true,
        });
      } else {
        await updateStoreSettings({
          storeSlug,
          depositType: mode,
          depositAmount: resolvedAmount!,
          includeShippingInOrderDeposit: includeShippingOnTop,
        });
      }
      setSuccess(true);
    } catch (caughtError) {
      setError(
        caughtError instanceof Error
          ? caughtError.message
          : t(keys.errors.generic),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Live preview line
  function getPreview(): string | null {
    if (mode === "none") return null;
    if (mode === "shipping") return t(keys.seller.depositPreviewShippingOnly);
    if (mode === "flat") {
      if (resolvedAmount === null) return null;
      return includeShippingOnTop
        ? t(keys.seller.depositPreviewFlatPlusShipping, {
            amount: String(resolvedAmount),
          })
        : t(keys.seller.depositPreviewFlat, { amount: String(resolvedAmount) });
    }
    if (mode === "percent") {
      if (resolvedAmount === null) return null;
      return includeShippingOnTop
        ? t(keys.seller.depositPreviewPercentPlusShipping, {
            percent: String(resolvedAmount),
          })
        : t(keys.seller.depositPreviewPercent, {
            percent: String(resolvedAmount),
          });
    }
    return null;
  }

  const preview = getPreview();
  const percentCallout =
    mode === "percent" ? getPercentCallout(t, amountInput) : null;

  const ALL_MODES: DepositMode[] = ["none", "shipping", "flat", "percent"];

  function modeLabel(m: DepositMode): string {
    if (m === "none") return t(keys.seller.depositModeChipNone);
    if (m === "shipping") return t(keys.seller.depositModeChipShipping);
    if (m === "flat") return t(keys.seller.depositModeChipFlat);
    return t(keys.seller.depositModeChipPercent);
  }

  function modeDescription(m: DepositMode): string {
    if (m === "none") return t(keys.seller.depositNoneHelper);
    if (m === "shipping") return t(keys.seller.depositShippingHelper);
    if (m === "flat") return t(keys.seller.depositFlatHelper);
    return t(keys.seller.depositModeCustomHelper);
  }

  return (
    <div className="flex flex-col gap-5">
      {error ? (
        <Alert message={error} helperText={t(keys.errors.recovery)} />
      ) : null}
      {success ? (
        <SuccessNotice title={t(keys.seller.depositSettingsSavedNotice)} />
      ) : null}

      {/* Mode selector — radio option cards */}
      <div
        role="radiogroup"
        className="divide-y divide-[color:var(--border-light)] overflow-hidden rounded-xl border border-[color:var(--border-light)]"
      >
        {ALL_MODES.map((m) => {
          const isActive = mode === m;
          return (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={isActive}
              onClick={() => handleModeChange(m)}
              className={[
                "flex w-full items-start gap-3 px-4 py-3.5 text-start transition-colors",
                isActive
                  ? "bg-[color:var(--surface-alt)]"
                  : "bg-[color:var(--surface)] hover:bg-[color:var(--surface-alt)]",
              ].join(" ")}
            >
              {/* Radio indicator */}
              <span
                className={[
                  "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
                  isActive
                    ? "border-[color:var(--primary-500)] bg-[color:var(--primary-500)]"
                    : "border-[color:var(--border-strong)] bg-[color:var(--surface)]",
                ].join(" ")}
              >
                {isActive && (
                  <span className="h-1.5 w-1.5 rounded-full bg-white" />
                )}
              </span>
              <div className="flex flex-col gap-0.5">
                <span
                  className={`text-sm font-semibold ${
                    isActive
                      ? "text-[color:var(--primary-500)]"
                      : "text-[color:var(--text-primary)]"
                  }`}
                >
                  {modeLabel(m)}
                </span>
                <span className="text-xs text-[color:var(--text-muted)]">
                  {modeDescription(m)}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Flat / Percent input panel — always in DOM, height-animated */}
      <div
        className={[
          "grid transition-all duration-300 ease-in-out",
          mode === "flat" || mode === "percent"
            ? "grid-rows-[1fr] opacity-100"
            : "grid-rows-[0fr] opacity-0",
        ].join(" ")}
      >
        <div className="overflow-hidden">
          <div className="flex flex-col gap-3">
            <FormField
              name={mode === "flat" ? "depositAmount" : "depositPercent"}
              label={
                mode === "flat"
                  ? t(keys.seller.depositFlatLabel)
                  : t(keys.seller.depositPercentLabel)
              }
              error={amountError ?? undefined}
            >
              <div className="relative">
                <NumberInput
                  name={mode === "flat" ? "depositAmount" : "depositPercent"}
                  allowDecimal
                  min={0}
                  max={mode === "percent" ? 100 : undefined}
                  step={0.01}
                  value={amountInput}
                  hasError={Boolean(amountError)}
                  onChange={(e) => {
                    setSuccess(false);
                    setAmountInput(e.target.value);
                  }}
                />
                <span className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-[color:var(--text-muted)]">
                  {mode === "flat" ? "EGP" : "%"}
                </span>
              </div>
            </FormField>

            {/* Percent helper — plain muted text, no box */}
            {mode === "percent" && percentCallout ? (
              <p className="text-xs leading-relaxed text-[color:var(--text-muted)]">
                {percentCallout}
              </p>
            ) : null}

            {/* Inline: add shipping on top */}
            <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-3">
              <Checkbox
                size="sm"
                className="mt-0.5"
                checked={includeShippingOnTop}
                disabled={isSubmitting}
                onChange={(e) => {
                  setSuccess(false);
                  setIncludeShippingOnTop(e.target.checked);
                }}
              />
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium text-[color:var(--text-primary)]">
                  {t(keys.seller.depositIncludeShippingOnTopLabel)}
                </span>
                {includeShippingOnTop ? (
                  <span className="text-xs text-[color:var(--text-muted)]">
                    {t(keys.seller.depositIncludeShippingOnTopHelper)}
                  </span>
                ) : null}
              </div>
            </label>
          </div>
        </div>
      </div>

      {/* Live preview — always in DOM, height-animated */}
      <div
        className={[
          "grid transition-all duration-300 ease-in-out",
          preview
            ? "grid-rows-[1fr] opacity-100"
            : "grid-rows-[0fr] opacity-0",
        ].join(" ")}
      >
        <div className="overflow-hidden">
          <div className="rounded-xl bg-[color:var(--surface-alt)] px-4 py-3 text-sm font-medium text-[color:var(--text-primary)]">
            {preview ?? ""}
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={
            isSubmitting ||
            success ||
            ((mode === "flat" || mode === "percent") && Boolean(amountError))
          }
          variant="primary"
          size="md"
        >
          {t(keys.common.save)}
        </Button>
      </div>

      <ConfirmDialog
        isOpen={showConfirm}
        title={t(keys.seller.depositSaveConfirmTitle)}
        body={t(keys.seller.depositSaveConfirmBody)}
        confirmLabel={t(keys.seller.depositSaveConfirmButton)}
        cancelLabel={t(keys.common.cancel)}
        isSubmitting={isSubmitting}
        intent="confirm"
        onConfirm={handleConfirmedSubmit}
        onCancel={() => setShowConfirm(false)}
      />
    </div>
  );
}
